/*
    Multi-selector with all contacts on the loan
*/

import { useQuery } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import { useMemo } from 'react';
import { Control, Controller, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { GetContactsForLoanDocument } from '@willow/graphql-iso/src/app';
import { getBorrowerNameWithoutMiddleName } from '@willow/shared-iso';
import { AuthorizedContactId, BorrowerId, CompanyId, LoanId } from '@willow/types-iso';

import { assertNever } from '../../../utils';
import { Loader } from '../../Loader/Loader';
import { WillowMultiSelect } from './WillowSelect';

type LoanContactId = BorrowerId | AuthorizedContactId;
type Props<T extends FieldValues> = {
  companyId: CompanyId;
  loanId: LoanId;
  fieldName: FieldPathByValue<T, LoanContactId[] | undefined>;
  control: Control<T>;
  allowAllSelections?: boolean; // some components can select even if email / consent is not provided
  allowAllThirdParty?: boolean; // ability to select borrowers w/o email or consent, but disable third-party missing those attributes
  hideErrorMessage?: boolean;
};

export function ControlledLoanContactMultiSelect<T extends FieldValues>({
  companyId,
  loanId,
  fieldName,
  control,
  allowAllSelections = true,
  allowAllThirdParty = false,
  hideErrorMessage = false,
}: Props<T>) {
  const {
    formState: { errors },
  } = useController({ control, name: fieldName });

  const { loading, data } = useQuery(GetContactsForLoanDocument, {
    variables: { companyId, loanId },
  });

  const selectOptions = useMemo(() => {
    if (!data) {
      return [];
    }
    const {
      company: {
        loan: { primaryBorrower, additionalBorrowers, authorizedContacts },
      },
    } = data;
    const contacts = [primaryBorrower, ...additionalBorrowers, ...authorizedContacts];
    return [
      ...contacts.map((contact) => {
        let name;
        switch (contact.__typename) {
          case 'ThirdPartyAuthorizedContact':
            name = `${contact.name.firstName} ${contact.name.lastName}`;
            break;
          case 'Borrower':
            name = getBorrowerNameWithoutMiddleName(contact);
            break;
          case 'BuilderAuthorizedContact':
            name = contact.builderCompanyName;
            break;
          default:
            assertNever(contact);
        }

        let disabledMessage;
        if (!contact.email) {
          disabledMessage = 'email not provided';
        } else if (
          (contact.__typename === 'Borrower' || contact.__typename === 'ThirdPartyAuthorizedContact') &&
          !contact.emailConsent
        ) {
          disabledMessage = 'email consent disabled';
        }

        return {
          value: contact.id,
          label: (
            <>
              {name} {disabledMessage && <span className="u-color-red1 fst-italic">({disabledMessage})</span>}
            </>
          ),
          isDisabled:
            (!allowAllSelections ||
              ((contact.__typename === 'ThirdPartyAuthorizedContact' ||
                contact.__typename === 'BuilderAuthorizedContact') &&
                !allowAllThirdParty)) &&
            disabledMessage != null,
        };
      }),
    ];
  }, [data, allowAllSelections, allowAllThirdParty]);

  if (loading || data == null) {
    return <Loader />;
  }

  return (
    <>
      <Controller
        name={fieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <WillowMultiSelect
            placeholder="Select contact(s)"
            options={selectOptions}
            value={value ? selectOptions.filter((r) => value.includes(r.value)) : []}
            onChange={(contact) => {
              onChange(contact ? contact.map((c) => c.value) : []);
            }}
          />
        )}
      />
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
